import api from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/constants';

export enum SupplierReturnStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  ACKNOWLEDGED = 'acknowledged',
  REFUNDED = 'refunded',
  REPLACED = 'replaced',
  REJECTED = 'rejected',
}

export enum SupplierReturnReason {
  DEFECT = 'defect',
  WRONG_ITEM = 'wrong_item',
  EXCESS = 'excess',
  OTHER = 'other',
}

export interface SupplierReturnItem {
  id: number;
  item_id: number;
  item_name: string;
  item_sku: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
}

export interface SupplierReturnResponse {
  id: number;
  return_number: string;
  supplier_id: number;
  supplier_name: string;
  purchase_order_id?: number;
  po_number?: string;
  reason: SupplierReturnReason;
  status: SupplierReturnStatus;
  notes?: string;
  total_amount: number;
  created_by: number;
  approved_by?: number;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  items: SupplierReturnItem[];
}

export interface SupplierReturnCreate {
  supplier_id: number;
  purchase_order_id?: number;
  reason: SupplierReturnReason;
  notes?: string;
  items: {
    item_id: number;
    quantity: number;
    unit_price: number;
  }[];
}

export interface SupplierReturnUpdate {
  status?: SupplierReturnStatus;
  notes?: string;
}

const API_URL = `${API_BASE_URL}/supplier-returns`;

// Fetch all supplier returns
export const fetchSupplierReturns = async (params?: {
  supplier_id?: number;
  status?: SupplierReturnStatus;
  reason?: SupplierReturnReason;
  page?: number;
  page_size?: number;
}): Promise<SupplierReturnResponse[]> => {
  const response = await api.get<SupplierReturnResponse[]>(API_URL, { params });
  return response.data;
};

// Get supplier return by ID
export const getSupplierReturnById = async (id: number): Promise<SupplierReturnResponse> => {
  const response = await api.get<SupplierReturnResponse>(`${API_URL}/${id}`);
  return response.data;
};

// Create new supplier return
export const createSupplierReturn = async (data: SupplierReturnCreate): Promise<SupplierReturnResponse> => {
  const response = await api.post<SupplierReturnResponse>(API_URL, data);
  return response.data;
};

// Update supplier return
export const updateSupplierReturn = async (
  id: number,
  data: SupplierReturnUpdate
): Promise<SupplierReturnResponse> => {
  const response = await api.patch<SupplierReturnResponse>(`${API_URL}/${id}`, data);
  return response.data;
};

// Send supplier return to supplier (change status to SENT)
export const sendSupplierReturn = async (id: number): Promise<SupplierReturnResponse> => {
  const response = await api.post<SupplierReturnResponse>(`${API_URL}/${id}/send`);
  return response.data;
};

// Acknowledge supplier return (SENT → ACKNOWLEDGED)
export const acknowledgeSupplierReturn = async (id: number): Promise<SupplierReturnResponse> => {
  const response = await api.post<SupplierReturnResponse>(`${API_URL}/${id}/acknowledge`);
  return response.data;
};

// Refund supplier return (ACKNOWLEDGED → REFUNDED)
export const refundSupplierReturn = async (id: number): Promise<SupplierReturnResponse> => {
  const response = await api.post<SupplierReturnResponse>(`${API_URL}/${id}/refund`);
  return response.data;
};

// Replace supplier return (ACKNOWLEDGED → REPLACED)
export const replaceSupplierReturn = async (id: number): Promise<SupplierReturnResponse> => {
  const response = await api.post<SupplierReturnResponse>(`${API_URL}/${id}/replace`);
  return response.data;
};

// Reject supplier return
export const rejectSupplierReturn = async (id: number, notes: string): Promise<SupplierReturnResponse> => {
  const response = await api.post<SupplierReturnResponse>(`${API_URL}/${id}/reject`, { notes });
  return response.data;
};
